import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  Copy,
  ChevronDown,
  ChevronUp,
  Star,
  Check,
  DollarSign,
  Layers,
  Sparkles,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { PageSection, PricingPlanItem, CurrencyCode, PricePeriod, ButtonLinkType } from '../../../../types';
import { ActionLinkInput } from '../common/ActionLinkInput';

interface PricingEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const PricingEditor: React.FC<PricingEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};
  const items: PricingPlanItem[] = content.items || [
    {
      id: '1',
      title: 'Standart Paket',
      priceAmount: 15000,
      priceCurrency: 'TRY',
      pricePeriod: 'month',
      description: 'Küçük ölçekli projeler için ideal temel paket.',
      features: ['Temel Danışmanlık', 'Aylık Raporlama', 'E-posta Desteği'],
      isPopular: false,
      buttonText: 'Hemen Başla',
      buttonLink: '/iletisim',
      buttonLinkType: 'page',
    },
    {
      id: '2',
      title: 'Kurumsal Pro',
      priceAmount: 35000,
      priceCurrency: 'TRY',
      pricePeriod: 'month',
      description: 'Geniş kapsamlı yönetim ve stratejik büyüme çözümleri.',
      features: ['Tam Kapsamlı Yönetim', 'Haftalık Strateji Toplantısı', '7/24 Özel Danışman', 'Öncelikli Operasyon'],
      isPopular: true,
      badgeText: 'EN ÇOK TERCİH EDİLEN',
      buttonText: 'Teklif Al & Başla',
      buttonLink: '/iletisim',
      buttonLinkType: 'page',
    },
  ];

  const [activeCardId, setActiveCardId] = useState<string | null>(items[0]?.id || null);

  const handleUpdateItem = (index: number, updates: Partial<PricingPlanItem>) => {
    const nextItems = [...items];
    nextItems[index] = { ...nextItems[index], ...updates };
    onUpdateContent({ items: nextItems });
  };

  const handleAddItem = () => {
    const newItem: PricingPlanItem = {
      id: `plan_${Date.now().toString(36)}`,
      title: `Yeni Paket ${items.length + 1}`,
      priceAmount: 990,
      priceCurrency: 'TRY',
      pricePeriod: 'month',
      description: 'Bu paketin sağladığı temel avantajlar ve hedefler.',
      features: ['Hosting & SSL Dahil', 'Hızlı Destek', 'Mobil Uyumlu Tasarım'],
      isPopular: false,
      buttonText: 'Teklif Al',
      buttonLink: '/iletisim',
      buttonLinkType: 'page',
    };
    const nextItems = [...items, newItem];
    onUpdateContent({ items: nextItems });
    setActiveCardId(newItem.id);
  };

  const handleDuplicateItem = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const source = items[index];
    const duplicated: PricingPlanItem = {
      ...JSON.parse(JSON.stringify(source)),
      id: `plan_${Date.now().toString(36)}`,
      title: `${source.title} (Kopya)`,
      isPopular: false,
    };
    const nextItems = [...items];
    nextItems.splice(index + 1, 0, duplicated);
    onUpdateContent({ items: nextItems });
    setActiveCardId(duplicated.id);
  };

  const handleDeleteItem = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const nextItems = items.filter((_, i) => i !== index);
    onUpdateContent({ items: nextItems });
    if (activeCardId === items[index]?.id) {
      setActiveCardId(nextItems[0]?.id || null);
    }
  };

  const handleMoveCard = (index: number, direction: 'up' | 'down', e: React.MouseEvent) => {
    e.stopPropagation();
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;
    const nextItems = [...items];
    const [moved] = nextItems.splice(index, 1);
    nextItems.splice(targetIndex, 0, moved);
    onUpdateContent({ items: nextItems });
  };

  const handleAddFeature = (planIndex: number) => {
    const currentFeatures = items[planIndex]?.features || [];
    const nextFeatures = [...currentFeatures, `Yeni Özellik ${currentFeatures.length + 1}`];
    handleUpdateItem(planIndex, { features: nextFeatures });
  };

  const handleUpdateFeature = (planIndex: number, featureIndex: number, text: string) => {
    const currentFeatures = [...(items[planIndex]?.features || [])];
    currentFeatures[featureIndex] = text;
    handleUpdateItem(planIndex, { features: currentFeatures });
  };

  const handleDeleteFeature = (planIndex: number, featureIndex: number) => {
    const currentFeatures = (items[planIndex]?.features || []).filter((_, i) => i !== featureIndex);
    handleUpdateItem(planIndex, { features: currentFeatures });
  };

  const handleMoveFeature = (planIndex: number, featureIndex: number, direction: 'up' | 'down') => {
    const currentFeatures = [...(items[planIndex]?.features || [])];
    const targetIdx = direction === 'up' ? featureIndex - 1 : featureIndex + 1;
    if (targetIdx < 0 || targetIdx >= currentFeatures.length) return;
    const [moved] = currentFeatures.splice(featureIndex, 1);
    currentFeatures.splice(targetIdx, 0, moved);
    handleUpdateItem(planIndex, { features: currentFeatures });
  };

  return (
    <div className="space-y-5">
      {/* Section Header Fields */}
      <div className="p-3.5 sm:p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Bölüm Başlıkları</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge ?? 'FİYATLANDIRMA PAKETLERİ'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            placeholder="Örn: FİYATLANDIRMA"
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title ?? 'İhtiyacınıza Uygun Esnek Planlar'}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            placeholder="Başlık girin..."
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Açıklama</label>
          <textarea
            value={content.description ?? ''}
            onChange={(e) => onUpdateContent({ description: e.target.value })}
            rows={2}
            placeholder="Bölüm hakkında kısa açıklama..."
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-300 p-2.5 rounded-xl outline-none focus:border-blue-500 resize-none"
          />
        </div>
      </div>

      {/* Plan Cards List Header */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-400" />
            <h4 className="text-xs font-extrabold text-slate-200">Fiyat Paketleri ({items.length})</h4>
          </div>
          <button
            type="button"
            onClick={handleAddItem}
            className="px-3 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-bold flex items-center gap-1.5 shadow-sm transition active:scale-95"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Paket Ekle</span>
          </button>
        </div>

        {/* Individual Cards Accordion */}
        <div className="space-y-2.5">
          {items.map((plan, pIdx) => {
            const isExpanded = activeCardId === plan.id;
            const isCustom = Boolean(plan.customPriceText);

            return (
              <div
                key={plan.id || pIdx}
                className={`rounded-2xl border transition-all ${
                  isExpanded
                    ? 'bg-slate-800/95 border-blue-500/60 shadow-lg ring-1 ring-blue-500/20'
                    : 'bg-slate-800/40 border-slate-700/60 hover:border-slate-600'
                }`}
              >
                {/* Header Row */}
                <div
                  onClick={() => setActiveCardId(isExpanded ? null : plan.id)}
                  className="p-3 flex items-center justify-between cursor-pointer select-none"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-6 h-6 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center text-[10px] font-black text-slate-300 shrink-0">
                      {pIdx + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="text-xs font-bold text-slate-200 truncate flex items-center gap-2">
                        <span>{plan.title || 'İsimsiz Paket'}</span>
                        {plan.isPopular && (
                          <span className="px-1.5 py-0.5 rounded-md bg-blue-500/20 text-blue-300 text-[9px] font-black shrink-0">
                            ÖNE ÇIKAN
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-emerald-400 font-bold font-mono">
                        {plan.customPriceText
                          ? plan.customPriceText
                          : `${(plan.priceAmount ?? 0).toLocaleString('tr-TR')} ${plan.priceCurrency || 'TRY'} / ${plan.pricePeriod || 'ay'}`}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      disabled={pIdx === 0}
                      onClick={(e) => handleMoveCard(pIdx, 'up', e)}
                      title="Yukarı Taşı"
                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-20 transition"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={pIdx === items.length - 1}
                      onClick={(e) => handleMoveCard(pIdx, 'down', e)}
                      title="Aşağı Taşı"
                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg disabled:opacity-20 transition"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDuplicateItem(pIdx, e)}
                      title="Paketi Kopyala"
                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteItem(pIdx, e)}
                      title="Paketi Sil"
                      className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="p-1 text-slate-400">
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-blue-400" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                </div>

                {/* Expanded Card Details */}
                {isExpanded && (
                  <div className="p-3.5 sm:p-4 pt-2 border-t border-slate-700/50 space-y-4">
                    {/* Title & Subtitle */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Paket Adı</label>
                        <input
                          type="text"
                          value={plan.title || ''}
                          onChange={(e) => handleUpdateItem(pIdx, { title: e.target.value })}
                          placeholder="Örn: Premium Paket"
                          className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Alt Başlık (Opsiyonel)</label>
                        <input
                          type="text"
                          value={plan.subtitle || ''}
                          onChange={(e) => handleUpdateItem(pIdx, { subtitle: e.target.value })}
                          placeholder="Örn: Büyüyen işletmeler için"
                          className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Price Type Switcher */}
                    <div className="p-3 rounded-2xl bg-slate-900/80 border border-slate-700 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Fiyat Formatı</span>
                        <div className="flex items-center gap-1 bg-slate-800 p-0.5 rounded-lg border border-slate-700 text-[10px] font-bold">
                          <button
                            type="button"
                            onClick={() => handleUpdateItem(pIdx, { customPriceText: undefined, priceAmount: plan.priceAmount || 990 })}
                            className={`px-2.5 py-1 rounded-md transition ${
                              !isCustom ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'
                            }`}
                          >
                            Rakam
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUpdateItem(pIdx, { customPriceText: plan.customPriceText || 'Özel Teklif' })}
                            className={`px-2.5 py-1 rounded-md transition ${
                              isCustom ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'
                            }`}
                          >
                            Özel Metin
                          </button>
                        </div>
                      </div>

                      {/* Structured Price Inputs (Mobile optimized) */}
                      {!isCustom ? (
                        <div className="space-y-3">
                          {/* Numeric Price Input */}
                          <div className="space-y-1">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                              Fiyat Tutarı
                            </label>
                            <div className="flex items-center gap-2">
                              <div className="relative flex-1">
                                <input
                                  type="number"
                                  inputMode="numeric"
                                  value={plan.priceAmount !== undefined ? plan.priceAmount : ''}
                                  onChange={(e) => {
                                    const rawVal = e.target.value;
                                    if (rawVal === '') {
                                      handleUpdateItem(pIdx, { priceAmount: 0 });
                                    } else {
                                      const parsed = parseFloat(rawVal);
                                      if (!isNaN(parsed)) {
                                        handleUpdateItem(pIdx, { priceAmount: parsed });
                                      }
                                    }
                                  }}
                                  placeholder="Örn: 990"
                                  className="w-full bg-slate-800 border border-slate-700 text-sm font-black text-emerald-400 px-3.5 py-2 rounded-xl outline-none focus:border-blue-500 font-mono"
                                />
                              </div>

                              {/* Quick increment buttons */}
                              <button
                                type="button"
                                onClick={() => handleUpdateItem(pIdx, { priceAmount: (plan.priceAmount || 0) + 100 })}
                                className="px-2.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition"
                              >
                                +100
                              </button>
                              <button
                                type="button"
                                onClick={() => handleUpdateItem(pIdx, { priceAmount: (plan.priceAmount || 0) + 500 })}
                                className="px-2.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition"
                              >
                                +500
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2.5">
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Para Birimi</label>
                              <select
                                value={plan.priceCurrency || 'TRY'}
                                onChange={(e) => handleUpdateItem(pIdx, { priceCurrency: e.target.value as CurrencyCode })}
                                className="w-full bg-slate-800 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500 cursor-pointer"
                              >
                                <option value="TRY">₺ TL (TRY)</option>
                                <option value="EUR">€ Euro (EUR)</option>
                                <option value="USD">$ Dolar (USD)</option>
                                <option value="GBP">£ Sterlin (GBP)</option>
                              </select>
                            </div>

                            <div className="space-y-1">
                              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Periyot</label>
                              <select
                                value={plan.pricePeriod || 'month'}
                                onChange={(e) => handleUpdateItem(pIdx, { pricePeriod: e.target.value as PricePeriod })}
                                className="w-full bg-slate-800 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500 cursor-pointer"
                              >
                                <option value="month">Aylık (/ ay)</option>
                                <option value="year">Yıllık (/ yıl)</option>
                                <option value="day">Günlük (/ gün)</option>
                                <option value="week">Haftalık (/ hafta)</option>
                                <option value="one_time">Tek Seferlik</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Görünecek Özel Metin</label>
                          <input
                            type="text"
                            value={plan.customPriceText || 'Özel Teklif'}
                            onChange={(e) => handleUpdateItem(pIdx, { customPriceText: e.target.value })}
                            placeholder="Örn: Özel Teklif / İletişime Geçin"
                            className="w-full bg-slate-800 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                          />
                        </div>
                      )}
                    </div>

                    {/* Short Description */}
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Açıklama</label>
                      <textarea
                        value={plan.description || ''}
                        onChange={(e) => handleUpdateItem(pIdx, { description: e.target.value })}
                        rows={2}
                        placeholder="Paket detayları..."
                        className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2.5 rounded-xl outline-none focus:border-blue-500 resize-none"
                      />
                    </div>

                    {/* Popular Badge Switch */}
                    <div className="p-3 rounded-2xl bg-slate-900/60 border border-slate-700 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Star className={`w-3.5 h-3.5 ${plan.isPopular ? 'text-amber-400 fill-amber-400' : 'text-slate-400'}`} />
                          <span className="text-xs font-bold text-slate-200">Öne Çıkan / Popüler Paket</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={Boolean(plan.isPopular)}
                          onChange={(e) => handleUpdateItem(pIdx, { isPopular: e.target.checked })}
                          className="w-4 h-4 accent-blue-600 rounded cursor-pointer"
                        />
                      </div>
                      {plan.isPopular && (
                        <div className="space-y-1 pt-1">
                          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Rozet Metni</label>
                          <input
                            type="text"
                            value={plan.badgeText || 'EN ÇOK TERCİH EDİLEN'}
                            onChange={(e) => handleUpdateItem(pIdx, { badgeText: e.target.value })}
                            placeholder="EN ÇOK TERCİH EDİLEN"
                            className="w-full bg-slate-800 border border-slate-700 text-xs text-slate-200 px-3 py-1.5 rounded-xl outline-none focus:border-blue-500"
                          />
                        </div>
                      )}
                    </div>

                    {/* Features Checklist (Mobile-friendly list editor with up/down/delete) */}
                    <div className="space-y-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          Dahil Olan Özellikler ({plan.features?.length || 0})
                        </span>
                        <button
                          type="button"
                          onClick={() => handleAddFeature(pIdx)}
                          className="px-2 py-1 rounded-lg bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 text-[10px] font-bold flex items-center gap-1 transition active:scale-95"
                        >
                          <Plus className="w-3 h-3" />
                          <span>Özellik Ekle</span>
                        </button>
                      </div>

                      <div className="space-y-2">
                        {(plan.features || []).map((feat, fIdx) => (
                          <div
                            key={fIdx}
                            className="flex items-center gap-1.5 bg-slate-900/90 border border-slate-700/80 p-1.5 rounded-xl"
                          >
                            <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 ml-1" />
                            <input
                              type="text"
                              value={feat || ''}
                              onChange={(e) => handleUpdateFeature(pIdx, fIdx, e.target.value)}
                              placeholder="Özellik açıklaması..."
                              className="flex-1 bg-transparent text-xs text-slate-200 px-1.5 py-1 outline-none focus:text-white"
                            />
                            <div className="flex items-center gap-0.5 shrink-0">
                              <button
                                type="button"
                                disabled={fIdx === 0}
                                onClick={() => handleMoveFeature(pIdx, fIdx, 'up')}
                                className="p-1 text-slate-400 hover:text-white disabled:opacity-20 transition"
                                title="Yukarı"
                              >
                                <ArrowUp className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                disabled={fIdx === (plan.features?.length || 0) - 1}
                                onClick={() => handleMoveFeature(pIdx, fIdx, 'down')}
                                className="p-1 text-slate-400 hover:text-white disabled:opacity-20 transition"
                                title="Aşağı"
                              >
                                <ArrowDown className="w-3 h-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteFeature(pIdx, fIdx)}
                                className="p-1 text-slate-400 hover:text-rose-400 transition ml-0.5"
                                title="Sil"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* CTA Button Action */}
                    <ActionLinkInput
                      label="Kart Buton Eylemi"
                      buttonText={plan.buttonText || 'Teklif Al / Başla'}
                      onButtonTextChange={(txt) => handleUpdateItem(pIdx, { buttonText: txt })}
                      linkType={plan.buttonLinkType || 'page'}
                      onLinkTypeChange={(type: ButtonLinkType) => handleUpdateItem(pIdx, { buttonLinkType: type })}
                      linkValue={plan.buttonLink || '/iletisim'}
                      onLinkValueChange={(val) => handleUpdateItem(pIdx, { buttonLink: val })}
                      whatsappPhone={plan.whatsappPhone}
                      onWhatsappPhoneChange={(phone) => handleUpdateItem(pIdx, { whatsappPhone: phone })}
                      whatsappMessage={plan.whatsappMessage}
                      onWhatsappMessageChange={(msg) => handleUpdateItem(pIdx, { whatsappMessage: msg })}
                      phoneNumber={plan.phoneNumber}
                      onPhoneNumberChange={(num) => handleUpdateItem(pIdx, { phoneNumber: num })}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
